/* eslint-disable react-hooks/exhaustive-deps */
import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { inject, observer, PropTypes as MobXPropTypes } from 'mobx-react';
import PropTypes from 'prop-types';
import NewPaymentConfirmationTab from './NewPaymentConfirmationTab/NewPaymentConfirmationTab';
import PopUpSuccessScheme from '../PopUpScheme/PopUpSuccessScheme';
import { ClearJunctionTable, OpenPaydTable, LPBForm, CryptoWithdrawalForm } from './ProviderForms';
import { filterPaymentAccounts, findUserAccount, getPaymentMethodsOptions } from './utils';
import { getAccountTitleWithFormatting } from '../../utils';
import i18nContext from 'components/i18n-context';
import {
  PAYMENT_PROVIDERS,
  FORM_TYPES,
  PAYMENT_METHOD,
  CUSTOMER_PERMISSIONS,
  BACKEND_MODULE_TYPES
} from 'components/common/constants';
//eslint-disable-next-line max-len
import PopUpForApprovalTransactionConfirmationScheme from 'components/common/PopUpScheme/PopUpForApprovalTransactionConfirmationScheme';
import PopUpInternalActionConfirmationScheme from 'components/common/PopUpScheme/PopUpInternalActionConfirmationScheme';
import { debounce, amountFormattedValue, isCryptoProvider } from 'services/utils';
import { ROUTE_PATHS } from 'routes/constants';
import { NewContainer } from 'uikit/NewContainer/NewContainer';
import { InputDropDown } from 'uikit/InputDropDown/InputDropDown';
import { PopUp } from 'uikit/PopUp/PopUp';
import './PaymentForm.scss';

const SIMPLE_FORM = FORM_TYPES.SIMPLE;
const ADVANCED_FORM = FORM_TYPES.ADVANCED;

// Manual providers use shared IBANs for all customers
// Payments to these IBANs should not be treated as internal transfers
const MANUAL_PROVIDERS = [
  PAYMENT_PROVIDERS.MANUAL,
  PAYMENT_PROVIDERS.CONVERA,
  PAYMENT_PROVIDERS.CONVERA_MERCHANT,
  PAYMENT_PROVIDERS.CONVERA_API
];

const PaymentForm = ({
  customerNumber,
  userStore,
  paymentStore,
  betweenAccountsStore,
  currencyExchangeStore,
  isForApprovalTransactionEdit = false,
  resetPreparedTransactionData,
  backToForApprovalTable
}) => {
  const i18n = useContext(i18nContext);
  const navigate = useNavigate();
  const providerType = paymentStore.currentAccount?.transfer_provider;
  const [isFormResetAfterAccountChanging, setIsFormResetAfterAccountChanging] = useState(true);
  const isUserCanCreatePreparedTransaction = userStore.hasPermissions([
    CUSTOMER_PERMISSIONS.CREATE_PREPARED_TRANSACTION
  ]);
  const isUserCanSendTransaction = userStore.hasPermissions([CUSTOMER_PERMISSIONS.CREATE_TRANSACTION]);

  useEffect(() => {
    if (!paymentStore.isRepeatPayment) {
      paymentStore.resetPreparedTransactionData();
    }
  }, []);

  useEffect(() => {
    if (paymentStore.isSuccess) {
      userStore.getUserAccounts();
    }
  }, [paymentStore.isSuccess]);

  const activeTab = [PAYMENT_METHOD.SWIFT, PAYMENT_METHOD.TARGET2].includes(paymentStore.paymentMethod)
    ? ADVANCED_FORM
    : SIMPLE_FORM;

  // Preselecting account with Clear Junction provider
  useEffect(() => {
    const paymentAccounts = filterPaymentAccounts(userStore.userAccounts);
    const hasAccounts = paymentAccounts.length > 0;
    const clearJunctionAccount = paymentAccounts.find(
      ({ transfer_provider }) => transfer_provider === PAYMENT_PROVIDERS.CLEARJUNCTION
    );
    clearJunctionAccount && !paymentStore.currentAccount && paymentStore.setSelectedAccount(clearJunctionAccount);
    if (paymentStore.currentAccount) {
      handleAccountChange('', paymentStore.currentAccount.wallet_number);
    }
    if (!paymentStore.currentAccount && !clearJunctionAccount && hasAccounts) {
      handleAccountChange('', accountsOptions[0].key);
    }
  }, [userStore.userAccounts]);

  const accountsOptions = filterPaymentAccounts(userStore.userAccounts)?.map((account) => {
    const title = getAccountTitleWithFormatting(account);

    return {
      key: account?.wallet_number,
      value: title,
      currency: account?.currency,
      networkCode: account?.network
    };
  });

  const setProviderData = (data) => paymentStore.setProviderData(data);
  const calculateCommission = (customerNumber, data) => paymentStore.calculatePaymentCommission(customerNumber, data);
  const checkTransfer = (customerNumber, data) =>
    paymentStore.checkTransfer(customerNumber, data, isForApprovalTransactionEdit);
  const validateTransfer = (...args) => paymentStore.validateTransfer(...args);
  const validateForApprovalTransaction = (data) => paymentStore.validateForApprovalTransaction(data);
  const removePaymentFile = (fileId) => paymentStore.removePaymentFile(fileId);
  // eslint-disable-next-line max-len
  const validateIban = async (iban) => {
    const foundedUsedAccount = findUserAccount(userStore.userAccounts, iban, paymentStore?.currentAccount?.currency);

    // Check if the found account belongs to a manual provider
    const isManualProviderAccount =
      foundedUsedAccount && MANUAL_PROVIDERS.includes(foundedUsedAccount.transfer_provider);

    if (
      !paymentStore.isTemplateTransaction &&
      foundedUsedAccount &&
      !isManualProviderAccount && // Don't redirect for manual provider accounts
      foundedUsedAccount.currency === paymentStore.currentAccount.currency &&
      foundedUsedAccount.wallet_number !== paymentStore.currentAccount.wallet_number
    ) {
      betweenAccountsStore.resetStore();
      betweenAccountsStore.setFromToAccounts(paymentStore.currentAccount, foundedUsedAccount);
      betweenAccountsStore.setIsRedirectFromPaymentFormStatus(true);
      navigate(ROUTE_PATHS.BETWEEN_ACCOUNTS);
    } else if (
      !paymentStore.isTemplateTransaction &&
      foundedUsedAccount &&
      !isManualProviderAccount && // Don't redirect for manual provider accounts
      foundedUsedAccount.currency !== paymentStore.currentAccount.currency
    ) {
      currencyExchangeStore.resetExchangeStore();
      currencyExchangeStore.setFromToAccounts(paymentStore.currentAccount, foundedUsedAccount);
      currencyExchangeStore.setIsRepeatTransactionStatus(true);
      navigate(ROUTE_PATHS.CURRENCY_EXCHANGE);
    } else {
      await paymentStore.validateIban(customerNumber, paymentStore.currentAccount?.wallet_number, iban);
      // Return the store's just-updated credentials directly rather than relying on the caller's
      // own `ibanCredentials` prop, which is a snapshot captured at that render and won't reflect
      // this validation's result until a re-render happens — a real gap for a caller (e.g.
      // handleIbanComplete) that immediately triggers a check afterward in the same async chain.
      return { ibanCredentials: paymentStore.ibanCredentials };
    }
  };
  // GBP-local counterpart to validateIban above — no "belongs to one of your own accounts" redirect
  // here (out of scope for this pair, see spec §6.1), just the external validation call itself.
  const checkAccountNumberAndSortCode = async (accountNumber, sortCode) => {
    const isValid = await paymentStore.validateAccountNumberAndSortCode(
      customerNumber,
      paymentStore.currentAccount?.wallet_number,
      accountNumber,
      sortCode
    );
    // Same reasoning as validateIban above — hand back the fresh store value instead of making the
    // caller trust its own stale `ibanCredentials` prop for an immediate follow-up check.
    return { isValid, ibanCredentials: paymentStore.ibanCredentials };
  };
  const clearIbanCredentials = () => paymentStore.clearIbanCredentials();
  const setError = (error) => paymentStore.setError(error);
  const setIban = (iban) => paymentStore.setIban(iban);
  const setIsNonIban = (status) => paymentStore.setIsNonIban(status);
  const resetPaymentMethods = () => paymentStore.resetPaymentMethodInfo();
  const verifyPayment = (securityCode) => paymentStore.verifyPaymentSecurityCode(customerNumber, securityCode);
  const resendVerifyCode = () => paymentStore.resendPaymentSecurityCode();
  const checkCryptoTransfer = (customerNumber, data) => paymentStore.checkCryptoTransfer(customerNumber, data);
  const validateCryptoWalletAddress = (data) => paymentStore.validateRecipientCryptoWalletAddress(customerNumber, data);
  const validateCryptoTransfer = (...args) => paymentStore.validateCryptoTransfer(...args);
  const validateForApprovalCryptoTransaction = (data) => paymentStore.validateForApprovalCryptoTransaction(data);
  const verifyCryptoTransfer = (securityCode) => paymentStore.verifyCryptoSecurityCode(customerNumber, securityCode);
  const setIsRepeatPaymentStatus = (status) => paymentStore.setIsRepeatPaymentStatus(status);
  const setIsPreparedTransactionsCreation = (status) => paymentStore.setIsPreparedTransactionsCreation(status);
  const setIsShowingInternalActionConfirmation = (status) =>
    paymentStore.setIsShowingInternalActionConfirmation(status);
  const setIsPreparedTransactionsSuccessfullyCreated = (status) =>
    paymentStore.setIsPreparedTransactionsSuccessfullyCreated(status);

  const validationProps = {
    i18n,
    customerNumber,
    onChange: setProviderData,
    onSubmit: isForApprovalTransactionEdit
      ? isCryptoProvider(providerType)
        ? validateForApprovalCryptoTransaction
        : validateForApprovalTransaction
      : isCryptoProvider(providerType)
        ? validateCryptoTransfer
        : validateTransfer,
    previousTransaction: paymentStore.previousTransactionInfo,
    providerData: paymentStore.providerData,
    currency: paymentStore?.currentAccount?.currency,
    paymentMethod: paymentStore?.paymentMethod,
    providerType,
    ibanCredentials: paymentStore?.ibanCredentials,
    paymentPurposes: userStore.paymentPurposes
  };
  const formProps = {
    account: { ...paymentStore.currentAccount },
    commission: paymentStore.commission,
    isCommissionLoading: paymentStore.isCommissionLoading,
    paymentMethod: paymentStore.paymentMethod,
    resetPaymentMethods,
    uploadedFiles: paymentStore.uploadedFiles,
    removePaymentFile: removePaymentFile,
    customerNumber: customerNumber,
    calculateCommission,
    paymentPurposes: userStore.paymentPurposes,
    checkTransfer: isCryptoProvider(providerType) ? debounce(checkCryptoTransfer, 400) : debounce(checkTransfer, 400),
    validateForApprovalTransaction: isCryptoProvider(providerType)
      ? validateForApprovalCryptoTransaction
      : validateForApprovalTransaction,
    isRepeatPayment: paymentStore.isRepeatPayment,
    iban: paymentStore.iban,
    setIban,
    setIsNonIban,
    isInternalIban: paymentStore.isInternalIban,
    checkIban: validateIban,
    checkAccountNumberAndSortCode,
    validateCryptoWalletAddress,
    isIbanCheckLoading: paymentStore.isIbanCheckLoading,
    uploadDocuments: paymentStore.uploadDocuments(customerNumber),
    error: paymentStore.temporaryError || paymentStore.error,
    ibanValidationError: paymentStore.ibanValidationError,
    setError,
    isSuccess: paymentStore.isSuccess,
    isLoading: paymentStore.isLoading || paymentStore.isFileUploading,
    currency: paymentStore?.currentAccount?.currency,
    formType: activeTab,
    ibanCredentials: paymentStore?.ibanCredentials,
    ibanWarning: paymentStore?.ibanWarning,
    clearIbanCredentials,
    validationProps,
    countries: paymentStore?.currentAccount?.countries,
    providerType,
    isFormResetAfterAccountChanging,
    setIsFormResetAfterAccountChanging,
    setIsRepeatPaymentStatus,
    setIsPreparedTransactionsCreation,
    isForApprovalTransactionEdit,
    isUserCanCreatePreparedTransaction,
    isUserCanSendTransaction
  };

  const getProviderTable = (providerType) => {
    switch (providerType) {
      case PAYMENT_PROVIDERS.CLEARJUNCTION: {
        return <ClearJunctionTable {...formProps} />;
      }
      case PAYMENT_PROVIDERS.OPENPAYD: {
        return <OpenPaydTable {...formProps} />; // same as Manual, Ryvyl, Contomobile, H&H, Dukascopy and GuruPay
      }
      case PAYMENT_PROVIDERS.LPB: {
        return <LPBForm {...formProps} />;
      }
      case PAYMENT_PROVIDERS.MANUAL: {
        return <OpenPaydTable {...formProps} />; // same as OP, GuruPay, Contomobile, H&H, Dukascopy and Ryvyl
      }
      case PAYMENT_PROVIDERS.H_AND_H: {
        return <OpenPaydTable {...formProps} />; // same as OP, GuruPay, Contomobile, Manual, Dukascopy and Ryvyl
      }
      case PAYMENT_PROVIDERS.DUKASCOPY: {
        return <OpenPaydTable {...formProps} />; // same as OP, GuruPay, Contomobile, H&H, Manual and Ryvyl
      }
      case PAYMENT_PROVIDERS.CONVERA: {
        return <OpenPaydTable {...formProps} />; // same as OP, GuruPay, Contomobile, Manual, Dukascopy and Ryvyl
      }
      case PAYMENT_PROVIDERS.CONVERA_MERCHANT: {
        return <OpenPaydTable {...formProps} />; // same as CONVERA
      }
      case PAYMENT_PROVIDERS.CONVERA_API: {
        return <OpenPaydTable {...formProps} />; // same as CONVERA and CONVERA_MERCHANT
      }
      case PAYMENT_PROVIDERS.RYVYL: {
        return <OpenPaydTable {...formProps} />; // same as OP, GuruPay, Contomobile, H&H, Dukascopy and Manual
      }
      case PAYMENT_PROVIDERS.GRAPHEREX: {
        return <CryptoWithdrawalForm {...formProps} />;
      }
      case PAYMENT_PROVIDERS.GURU_PAY: {
        return <OpenPaydTable {...formProps} />; // same as OP. Ryvyl, Contomobile, H&H, Dukascopy and Manual
      }
      case PAYMENT_PROVIDERS.GURU_PAY_API: {
        return <OpenPaydTable {...formProps} />; // same as GURU_PAY
      }
      case PAYMENT_PROVIDERS.CONTOMOBILE: {
        return <OpenPaydTable {...formProps} />; // same as OP. Ryvyl, Manual, H&H, Dukascopy and Contomobile
      }
      default:
        return <div>{i18n.getMessage('transfer.provider.notFound')}</div>;
    }
  };

  const handleAccountChange = (name, data) => {
    const selectedAccount = filterPaymentAccounts(userStore.userAccounts).find(
      ({ wallet_number }) => wallet_number === data
    );

    if (
      paymentStore.currentAccount?.wallet_number !== selectedAccount?.wallet_number ||
      (!paymentStore.error && paymentStore?.availablePaymentMethods?.length === 0)
    ) {
      paymentStore.setAccountInfo(selectedAccount);
      setIsFormResetAfterAccountChanging(false);
    }
  };

  const handlePaymentMethodChange = (name, data) => {
    paymentStore.setPaymentMethod(data);
  };

  const getAvailableBalance = () =>
    paymentStore?.currentAccount &&
    i18n.getMessage('sendMoney.topLabel', {
      available:
        paymentStore?.currentAccount?.availableDetails?.displayAmount ||
        amountFormattedValue(paymentStore?.currentAccount.available, 2)
    });

  useEffect(() => {
    if (!paymentStore.isRepeatPayment) {
      paymentStore.resetPaymentMethodInfo();
    }
  }, [paymentStore.isNonIban]);

  const handleCloseInternalActionConfirmation = () => {
    setIsShowingInternalActionConfirmation(false);
    paymentStore.setIsShowSwitchConfirmationMethodScreen(false);
  };

  const handleBackButtonClick = () => {
    if (isForApprovalTransactionEdit) {
      paymentStore.resetPreparedTransactionData();
      backToForApprovalTable();
    } else {
      paymentStore.setIsShowingConfirmationTab(false);
    }
  };

  return (
    <NewContainer
      className={'send-money content-card-wrapper'}
      header={i18n.getMessage(isForApprovalTransactionEdit ? 'container.forApprovalEdit' : 'container.sendMoney')}
      isBackButton={paymentStore.isShowingConfirmationTab || isForApprovalTransactionEdit}
      handleBackButtonClick={handleBackButtonClick}
    >
      {paymentStore.isShowingConfirmationTab ? (
        <NewPaymentConfirmationTab
          isLoading={paymentStore.isLoading}
          isCryptoTransaction={isCryptoProvider(providerType)}
          totalCommissionDetails={paymentStore?.commission?.commissionDetails}
          amountDetails={paymentStore.serverTransactionData?.amountDetails}
          commissionDetails={paymentStore?.commission?.commissionDetails}
          transferData={{
            ...paymentStore.serverTransactionData,
            uploadedFiles: paymentStore.uploadedFiles
          }}
          onConfirm={
            isCryptoProvider(providerType)
              ? paymentStore.createCryptoTransfer(customerNumber)
              : paymentStore.createTransfer(customerNumber)
          }
          error={paymentStore.confirmationPopupError}
          handleEditTransferButtonClick={handleBackButtonClick}
          paymentPurposes={userStore.paymentPurposes}
          providerType={providerType}
        />
      ) : (
        <div className={'send-money-wrapper'}>
          <div className={'inputs-wrapper'}>
            <InputDropDown
              isRequired={true}
              bottomLabel={getAvailableBalance()}
              label={i18n.getMessage('transfer.form.accountNumber.label')}
              value={paymentStore.currentAccount?.wallet_number}
              currency={paymentStore.currentAccount?.currency}
              networkCode={paymentStore.currentAccount?.network || null}
              onChange={handleAccountChange}
              options={accountsOptions}
              isMulti={false}
              isCryptoWallet={isCryptoProvider(providerType)}
            />
            <InputDropDown
              isRequired={true}
              label={i18n.getMessage('transfer.form.paymentMethod.label')}
              value={paymentStore?.paymentMethod}
              onChange={handlePaymentMethodChange}
              options={getPaymentMethodsOptions(
                paymentStore?.availablePaymentMethods,
                paymentStore.currentAccount?.transfer_provider
              )}
              isMulti={false}
            />
          </div>
          {getProviderTable(providerType)}
        </div>
      )}

      <PopUp
        className={
          paymentStore.isSuccess
            ? 'transaction-success'
            : paymentStore.isPreparedTransactionsCreation || paymentStore.isPreparedTransactionsSuccessfullyCreated
              ? 'prepared-transactions'
              : paymentStore.isShowingInternalActionConfirmation
                ? 'action-confirmation-popup pop-up-card--transparent'
                : 'transaction-info'
        }
        show={
          paymentStore.isShowingInternalActionConfirmation ||
          paymentStore.isSuccess ||
          paymentStore.isPreparedTransactionsCreation ||
          paymentStore.isPreparedTransactionsSuccessfullyCreated
        }
        alignOnCenter={
          paymentStore.isSuccess ||
          paymentStore.isShowingInternalActionConfirmation ||
          paymentStore.isPreparedTransactionsCreation ||
          paymentStore.isPreparedTransactionsSuccessfullyCreated
        }
        onClose={async () => {
          if (paymentStore.isSuccess) {
            paymentStore.resetSuccess();
          } else if (paymentStore.isPreparedTransactionsCreation) {
            setIsPreparedTransactionsCreation(false);
          } else if (paymentStore.isShowingInternalActionConfirmation) {
            setIsShowingInternalActionConfirmation(false);
            paymentStore.setIsShowSwitchConfirmationMethodScreen(false);
          } else {
            if (paymentStore.isPreparedTransactionsSuccessfullyCreated) {
              if (isForApprovalTransactionEdit) {
                await resetPreparedTransactionData();
              } else {
                setIsPreparedTransactionsSuccessfullyCreated(false);
                navigate(ROUTE_PATHS.UNAPPROVED_PAYMENTS);
              }
            }
          }
        }}
      >
        {paymentStore.isSuccess ? (
          <PopUpSuccessScheme
            onClose={() => paymentStore.resetSuccess()}
            message={i18n.getMessage('popUp.message.moneyTransfer')}
          />
        ) : paymentStore.isPreparedTransactionsCreation || paymentStore.isPreparedTransactionsSuccessfullyCreated ? (
          <PopUpForApprovalTransactionConfirmationScheme
            onClose={async () => {
              if (paymentStore.isPreparedTransactionsCreation) {
                setIsPreparedTransactionsCreation(false);
              } else if (paymentStore.isPreparedTransactionsSuccessfullyCreated && !isForApprovalTransactionEdit) {
                setIsPreparedTransactionsSuccessfullyCreated(false);
                navigate(ROUTE_PATHS.UNAPPROVED_PAYMENTS);
              } else if (paymentStore.isPreparedTransactionsSuccessfullyCreated && isForApprovalTransactionEdit) {
                await resetPreparedTransactionData();
              }
            }}
            onConfirm={
              isForApprovalTransactionEdit
                ? () => paymentStore.updatePreparedTransaction(customerNumber, isCryptoProvider(providerType))
                : () => paymentStore.createPreparedTransaction(customerNumber, isCryptoProvider(providerType))
            }
            isLoading={paymentStore.isLoading}
            isSuccess={paymentStore.isPreparedTransactionsSuccessfullyCreated}
            isForApprovalTransactionEdit={isForApprovalTransactionEdit}
            error={paymentStore.preparedTransactionsError}
          />
        ) : paymentStore.isShowingInternalActionConfirmation ? (
          <PopUpInternalActionConfirmationScheme
            isLoading={paymentStore.isLoading}
            isShowSwitchConfirmationMethodScreen={paymentStore.isShowSwitchConfirmationMethodScreen}
            title={i18n.getMessage('transfer.popUp.actionConfirmation.title')}
            confirmButtonLabel={i18n.getMessage('transfer.popUp.actionConfirmation.button.confirm')}
            onClose={handleCloseInternalActionConfirmation}
            onConfirm={isCryptoProvider(providerType) ? verifyCryptoTransfer : verifyPayment}
            error={paymentStore.confirmationPopupError}
            clearError={() => paymentStore.clearConfirmationPopupError()}
            resendSecurityCode={() => resendVerifyCode()}
            setIsShowSwitchConfirmationMethodScreen={(status) =>
              paymentStore.setIsShowSwitchConfirmationMethodScreen(status)
            }
            handleForceSwitchToAnotherConfirmationType={paymentStore.forceSwitchToAnotherConfirmationType}
            confirmationId={paymentStore.confirmationId}
            module={BACKEND_MODULE_TYPES.PAYMENT}
          />
        ) : null}
      </PopUp>
    </NewContainer>
  );
};

PaymentForm.propTypes = {
  isForApprovalTransactionEdit: PropTypes.bool,
  resetPreparedTransactionData: PropTypes.func,
  customerNumber: PropTypes.string,
  backToForApprovalTable: PropTypes.func,
  userStore: MobXPropTypes.observableObject,
  paymentStore: MobXPropTypes.observableObject,
  betweenAccountsStore: MobXPropTypes.observableObject,
  currencyExchangeStore: MobXPropTypes.observableObject
};

export default inject((stores) => ({
  customerNumber: stores.userStore.userData.account?.account_number,
  userStore: stores.userStore,
  paymentStore: stores.paymentStore,
  betweenAccountsStore: stores.betweenAccountsStore,
  currencyExchangeStore: stores.currencyExchangeStore
}))(observer(PaymentForm));
